/**
 * Client-Side Encryption Service
 *
 * THIS IS THE CORE SECURITY COMPONENT OF CDEaaS.
 *
 * Security Design (for viva):
 * - ALL encryption and decryption happens HERE, in the browser
 * - The backend NEVER receives plaintext files or encryption keys
 * - We use AES-GCM via the Web Crypto API (browser-native)
 * - AES-GCM provides both confidentiality AND integrity (authenticated encryption)
 * - The encryption key is derived from a user-provided password using PBKDF2
 *
 * Security Levels:
 * - Standard:  AES-128-GCM + PBKDF2 100,000 iterations  (fast, still very secure)
 * - Enhanced:  AES-256-GCM + PBKDF2 100,000 iterations  (default, strong)
 * - Maximum:   AES-256-GCM + PBKDF2 600,000 iterations  (slowest, max brute-force resistance)
 */

/**
 * Security level configurations.
 * Each level defines the AES key length, PBKDF2 iterations, and display metadata.
 */
export const SECURITY_LEVELS = {
  standard: {
    id: 'standard',
    label: 'Standard',
    algorithm: 'AES-128-GCM',
    keyLength: 128,
    iterations: 100000,
    description: 'AES-128-GCM with 100K PBKDF2 iterations. Fast encryption, still highly secure for everyday use.',
    color: '#2563eb',
  },
  enhanced: {
    id: 'enhanced',
    label: 'Enhanced',
    algorithm: 'AES-256-GCM',
    keyLength: 256,
    iterations: 100000,
    description: 'AES-256-GCM with 100K PBKDF2 iterations. Military-grade encryption, recommended default.',
    color: '#16a34a',
  },
  maximum: {
    id: 'maximum',
    label: 'Maximum',
    algorithm: 'AES-256-GCM',
    keyLength: 256,
    iterations: 600000,
    description: 'AES-256-GCM with 600K PBKDF2 iterations. Maximum brute-force resistance. Slower key derivation.',
    color: '#7c3aed',
  },
};


/**
 * Generate a cryptographic key from a password using PBKDF2.
 *
 * @param {string} password - User's encryption password
 * @param {Uint8Array} salt - Random salt (16 bytes)
 * @param {number} keyLength - AES key length in bits (128 or 256)
 * @param {number} iterations - PBKDF2 iteration count
 * @returns {Promise<CryptoKey>} - AES-GCM key
 */
export async function deriveKey(password, salt, keyLength = 256, iterations = 100000) {
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: iterations,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: keyLength },
    false,
    ['encrypt', 'decrypt']
  );
}


/**
 * Encrypt a file using AES-GCM with configurable security level.
 *
 * @param {File} file - The plaintext file to encrypt
 * @param {string} password - User's encryption password
 * @param {string} level - Security level: 'standard', 'enhanced', or 'maximum'
 * @returns {Promise<Object>} Encryption result with metadata
 */
export async function encryptFile(file, password, level = 'enhanced') {
  const config = SECURITY_LEVELS[level] || SECURITY_LEVELS.enhanced;

  const fileData = await file.arrayBuffer();

  // Hash the original plaintext file for integrity verification after decryption
  const originalHashBuffer = await window.crypto.subtle.digest('SHA-256', fileData);
  const originalHash = arrayBufferToHex(originalHashBuffer);

  // Generate random IV (12 bytes for AES-GCM)
  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  // Generate random salt for PBKDF2 (16 bytes)
  const salt = window.crypto.getRandomValues(new Uint8Array(16));

  // Derive key using the configured level parameters
  const startTime = performance.now();
  const key = await deriveKey(password, salt, config.keyLength, config.iterations);
  const keyDerivationMs = Math.round(performance.now() - startTime);

  // Encrypt using AES-GCM
  const encStartTime = performance.now();
  const encryptedData = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv },
    key,
    fileData
  );
  const encryptionMs = Math.round(performance.now() - encStartTime);

  const encryptedBlob = new Blob([encryptedData]);

  // Hash the encrypted data for upload integrity verification
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', encryptedData);
  const fileHash = arrayBufferToHex(hashBuffer);

  return {
    encryptedBlob,
    iv: arrayBufferToHex(iv),
    salt: arrayBufferToHex(salt),
    fileHash,
    originalHash,
    // Metadata for backend storage and display
    algorithm: config.algorithm,
    keyLength: config.keyLength,
    iterations: config.iterations,
    securityLevel: config.id,
    // Benchmark timings
    benchmark: {
      keyDerivationMs,
      encryptionMs,
      totalMs: keyDerivationMs + encryptionMs,
    },
  };
}


/**
 * Decrypt a file using AES-GCM.
 *
 * @param {ArrayBuffer} encryptedData - The encrypted file data
 * @param {string} password - User's encryption password
 * @param {string} ivHex - Initialization Vector as hex string
 * @param {string} saltHex - PBKDF2 salt as hex string
 * @param {number} keyLength - AES key length (128 or 256), default 256
 * @param {number} iterations - PBKDF2 iterations used during encryption, default 100000
 * @returns {Promise<ArrayBuffer>} - The decrypted file data
 */
export async function decryptFile(encryptedData, password, ivHex, saltHex, keyLength = 256, iterations = 100000) {
  const iv = hexToArrayBuffer(ivHex);
  const salt = hexToArrayBuffer(saltHex);

  const key = await deriveKey(password, new Uint8Array(salt), keyLength, iterations);

  const decryptedData = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(iv) },
    key,
    encryptedData
  );

  return decryptedData;
}


// ── Utility Functions ───────────────────────────────────────────

function arrayBufferToHex(buffer) {
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function hexToArrayBuffer(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes.buffer;
}
